import { useEffect, useRef, useState } from "react";
import {
  capitalizeWords,
  minToHourConverter,
  slugToTitle,
} from "@/helpers/useCommonFunctions";
import { useFilter } from "@dxp-feed/algolia";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@dxp-feed/ui/Accordion";
import { Checkbox } from "@dxp-feed/ui/Checkbox";
import { Text } from "@dxp-feed/ui/Text";
import {
  useAppDispatch,
  useAppSelector,
  useCallback,
  useMemo,
} from "@/utils/hooks/reactUseCustomHooks";
import { setFilters, setPageFilters, setTotalTimeFilter } from "@/utils/redux/recipes/algoliaSlice";
import {
  setFlyerStart,
  setFlyerEnd,
  setFilters as setProductFilter,
  setComplianceFlag,
  setSource,
} from "@/utils/redux/productsListing";
import { GlobalSearchSectionsType } from "@/utils/types/algolia";
import {
  analyticsFilterClickEvent,
  analyticsViewFilterEvent,
} from "@/services/algolia/analytics";
import { algoliaConfig } from "@/services/algolia/config";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { decodeHtml } from "@/templates/recipes/common/commonRecipeFunctions";
import { useRefine } from "@/templates/provider/RefineContext";
import { REMOVETAG } from "@/utils/constant";
import useFilterViewAnalytics from "@/utils/hooks/useFilterViewAnalytics";
import { setFavouriteMessage } from "@/utils/redux/favouriteProduct";
import { setShoppingMessage } from "@/utils/redux/shoppingList";
import { setFilterData } from "@/utils/redux/fetchOffers";

type FilterAccordianProps = {
  readonly attribute: string;
  readonly name: string;
  readonly index: number;
  readonly type: GlobalSearchSectionsType;
};

function FilterAccordian({
  attribute,
  name,
  index,
  type,
}: FilterAccordianProps): any {
  const { items, refine, toggleShowMore } = useFilter({
    attribute,
    limit: 10,
    showMore: true,
    showMoreLimit: 1000,
    sortBy:
      attribute === "promotions.startDate" || attribute === "promotions.endDate" ||attribute==="promotions.promotionComplianceFlag" || attribute==="promotions.source"
        ? ["name:desc"]
        : ["isRefined"],
  });
  const { aemFilterData }: any = useAppSelector(
    (state) => state.getAemDataSlice
  );

  const [activePanelIndex, setActivePanelIndex] = useState<number | null>(null);
  const [loadMoreItems, setLoadMoreItems] = useState(true);
  const panelRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const defaultActiveKey = name === "Total Time" ? `item-${index}` : undefined;

  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const indexName = algoliaConfig.indexNames[type];
  const userSession = useAppSelector((state) => state.gigyaslice);
  const { filters } = useAppSelector((state) => state.algoliaDataSlice);
  const userId = userSession.uid ?? undefined;
  const { setRefineFunctions } = useRefine();
  const filterContainerRef = useRef<HTMLDivElement>(null);

  const { itemRefs } = useFilterViewAnalytics({
    items,
    containerRef: filterContainerRef,
    analyticsPayload: { kind: type, index: indexName, userToken: userId },
    filterStringCb: (index) => `${attribute}:${items[index]?.value}`,
  });

  useEffect(() => {
    if (attribute === "brand") {
      setRefineFunctions((prev) => ({ ...prev, brand: refine }));
    }
  }, [refine, setRefineFunctions]);

  const onChangeHandler = useCallback(
    (item: any, panelIndex: number) => {
      const filtersCount = Math.max(
        0,
        !item?.isRefined ? filters + 1 : filters - 1
      );
      dispatch(setPageFilters(type as GlobalSearchSectionsType));
      dispatch(setFilters(filtersCount));
      if (attribute == "brand") {
        dispatch(
          setProductFilter({
            filter: attribute,
            filterName: item?.value,
            filterValue: item?.value,
          })
        );
        dispatch(
          setFilterData({
            filter: "brand",
            filterName: item?.value,
            filterValue: item?.value,
          })
        );
        dispatch(setFavouriteMessage());
        dispatch(setShoppingMessage());
      }
      const newParams = new URLSearchParams(searchParams.toString());
      if (newParams.has("page")) {
        newParams.set("page", "1");
        router.push(`?${newParams.toString()}`);
      }
      refine(item?.value);
      analyticsFilterClickEvent({
        kind: type,
        index: indexName,
        filters: [`${attribute}:${item?.value}`],
        userToken: userId,
      });
      setActivePanelIndex(panelIndex);
      scrollToTop(panelIndex);
    },
    [
      dispatch,
      type,
      refine,
      indexName,
      attribute,
      userId,
      filters,
      searchParams,
    ]
  );
  const hasToggleMore = useRef(true);
  useEffect(() => {
    const isTotalTime = name === "Total Time";
    if (!isTotalTime) return;

    if (hasToggleMore.current) {
      toggleShowMore();
      hasToggleMore.current = false;
    }
    if (items.length) {
      dispatch(setTotalTimeFilter(items));
    }
  }, [items]);

  const decodedSlug = useMemo(() => {
    if (pathname.includes("recipe-tag")) {
      return slugToTitle(pathname.split("/").pop() as string);
    }
    return null;
  }, [pathname]);

  const accordianData = useMemo(() => {
    return items
      .filter(
        (item: any) =>
          !(
            name === "Tags" &&
            type === "recipes" &&
            REMOVETAG.includes(item?.label)
          )
      )
      .map((item: any, idx: number) => (
        <Text
          key={`checkbox-${index}-${idx}-${name}`}
          as="div"
          className="flex items-center gap-2"
          data-index={idx}
          // @ts-ignore
          ref={(el) => (itemRefs.current[idx] = el)}
          data-filter-for={`${type}-${name}-${attribute}`}
        >
          <Checkbox
            id={`checkbox-${index}-${idx}-${name}`}
            className="m-[15px] min-w-[18px] min-h-[18px]"
            checked={item.isRefined}
            disabled={item.value === decodedSlug}
            onChange={() => onChangeHandler(item, index)}
          />
          <label
            htmlFor={`checkbox-${index}-${idx}-${name}`}
            className="text-[14px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            {name === "Total Time"
              ? minToHourConverter(item?.label)
              : name === "Skill Level"
                ? capitalizeWords(item?.label)
                : decodeHtml(item?.label.replace(`"`, ""))}
            {name === "Categories" ? ` (${item?.count})` : ""}
          </label>
        </Text>
      ));
  }, [items, index, name, onChangeHandler, type]);

  /**
   * @description: Scroll to the top of the panel when clicked filter
   */
  const scrollToTop = (index: any) => {
    if (index !== null && panelRefs.current[index]) {
      const panelElement: any = panelRefs.current[index];
      const containerElement = panelElement.parentElement;
      if (containerElement) {
        const topPosition = panelElement.offsetTop - containerElement.offsetTop;
        containerElement.scrollTo({
          top: topPosition,
          behavior: "smooth",
        });
      }
    }
  };

  // Function to detect scroll and trigger `toggleShowMore`
  const showMoreItems = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 3) {
      setLoadMoreItems(false); // Set flag to load more items
      toggleShowMore(); // Load more items when reaching the bottom
    }
  };

  useEffect(() => {
    const panelElement = panelRefs.current[index]?.parentElement;
    if (loadMoreItems) {
      panelElement?.addEventListener("scroll", showMoreItems);
    }
    return () => panelElement?.removeEventListener("scroll", showMoreItems);
  }, [activePanelIndex, items]);


  if(name== "FlyerStartDate") dispatch(setFlyerStart(items));
  if(name== "FlyerEndDate") dispatch(setFlyerEnd(items));
  if(name== "FlyerComplianceFlag") dispatch(setComplianceFlag(items));
  if(name== "FlyerSource") dispatch(setSource(items));
  if (items.length === 0) return null;

  return (
    name != "FlyerStartDate" && name != "FlyerEndDate" && name != "FlyerComplianceFlag" && name != "FlyerSource" && name != "Total Time" && (!aemFilterData?.tags?.length ? name !== "Tags" : true) && 
    !attribute.includes("allergens") && (
      <Accordion
        className="flex flex-col gap-6 lg:gap-4 border-b border-grey350"
        defaultActiveKey={defaultActiveKey}
        multiplePanelOpen={true}
      >
        <AccordionItem id={`item-${index}`} key={`item-${index}`}>
          <AccordionHeader
            id={`item-${index}`}
            className="font-bold !px-0 pt-0 pb-2 text-[16px] lg:text-[14px] group [&_.demo-icon]:w-6 [&_.demo-icon]:h-6 [&_.demo-icon]:flex [&_.demo-icon]:items-center [&_.demo-icon]:justify-center"
          >
            {name}
          </AccordionHeader>
          <AccordionPanel
            customRef={filterContainerRef}
            id={`item-${index}`}
            className="[&>div]:pl-0 [&>div]:flex [&>div]:flex-col [&>div]:gap-2 [&>div]:py-0 [&>div]:pb-2 lg:[&>div]:py-2 [&>div]:border-t [&>div]:border-none lg:[&>div]:border-grey350 [&>div]:overflow-x-hidden [&>div]:overflow-y-auto [&>div]:max-h-[280px] [&>div]:scroll-bar-grey"
          >
            <div
              ref={(el) => (panelRefs.current[index] = el)}
              id={`item-${index}`}
            ></div>
            {accordianData}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    )
  );
}

export default FilterAccordian;
